import {
    BrowserRouter as Router,
    Switch,
    Route,
} from 'react-router-dom';
import React, {
    useMemo,
    useState,
    useCallback,
    useEffect
} from 'react';
import _ from 'lodash';
// Imported First to allow Jutro styles to be overridden
import './App.scss';
import { setComponentMapOverrides } from '@jutro/uiconfig';
import {
    DropdownMenuLink,
    useModal,
    ModalNextEmitter
} from '@jutro/components';
import { routeConfirmationModal } from '@jutro/router';
import { AppFloorPlan } from '@jutro/floorplan';
import { useTranslator } from '@jutro/locale';
import { Preferences } from 'gw-pages-platform-react';
import { DynamicRoute, useAuthentication, withAuthContextProvider } from 'gw-digital-auth-react';
import { LoginCapabilityComponentMap, LoginCapabilityComponents } from 'gw-portals-login-react';
import vmTranslator from 'gw-platform-translations';
import { ViewModelServiceFactory } from 'gw-portals-viewmodel-js';
import { ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { filterCapabilityRoutes } from 'gw-portals-config-js';
import { AccurateBreakpointPropagation } from 'gw-jutro-adapters-react';
import { ServiceRequestService } from 'gw-capability-servicerequest';
import {
    platformComponentMap,
    platformComponents,
    ImageComponent
} from 'gw-components-platform-react';
import { ErrorBoundary } from 'gw-portals-error-react';

import FaqPage from 'gw-capability-faq-react';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import ResetPasswordPage from '../pages/ResetPasswordPage/ResetPasswordPage';
import EntryPage from '../pages/EntryPage/EntryPage';
import LandingPage from '../pages/LandingPage/LandingPage';
import NagPageAX from '../pages/NagPage_AX/NagPage_AX';
import ServiceRequestDetailsPage from '../pages/ServiceRequestDetailsPage/ServiceRequestDetailsPage';
import FNOLWizardPageAX from '../pages/FNOLWizardPage_AX/FNOLWizardPage_AX';
import messages from './App.messages';
import appRoutes from './App.routes.metadata.json5';
import serviceRequestListAX from '../pages/ServiceRequestList_AX/ServiceRequestList_AX';
import NewServiceWizardPageAX from '../pages/NewServiceWizardPage_AX/NewServiceWizardPage_AX';

const { capabilitiesConfig, env } = appConfig;

const componentMap = {
    landingpage: LandingPage,
    nagpage: NagPageAX,
    faqpage: FaqPage,
    preferences: Preferences,
    servicerequestdetailspage: ServiceRequestDetailsPage,
    serviceRequestListComponentAX: serviceRequestListAX,
    fnolWizardPageAX: FNOLWizardPageAX,
    newServiceWizardPageAX: NewServiceWizardPageAX
};

setComponentMapOverrides(
    {
        ...platformComponentMap,
        ...LoginCapabilityComponentMap,
        // replace the native IMG component with a proxied version
        img: { component: 'img' },
    },
    {
        ...platformComponents,
        ...LoginCapabilityComponents,
        img: ImageComponent,
    }
);

const modalEmitter = new ModalNextEmitter();

function App() {
    const {
        showAlert
    } = useModal();

    const auth = useAuthentication();
    const translator = useTranslator();
    const [viewModelService, updateViewModelService] = useState(undefined);
    const [paymentAppURL, setPaymentAppURL] = useState(undefined);
    const [routesDelete, setRoutesDelete] = useState([]);
    const [filterRoutes, setFilterRoutes] = useState(appRoutes.routes);

    const removeRoute = useCallback(() => {
        const newArrayRoutes = [];
        filterRoutes.forEach((route) => {
            if (_.isUndefined(route.id) || !routesDelete.includes(route.id)) {
                newArrayRoutes.push(route);
            }
        });
        setFilterRoutes(newArrayRoutes);
    }, [routesDelete, filterRoutes]);

    const addRemoveRoute = useCallback((routeId) => {
        if (!routesDelete.includes(routeId)) {
            routesDelete.push(routeId);
            setRoutesDelete(routesDelete);
        }
        removeRoute();
    }, [routesDelete]);

    const routesAuthority = useCallback(() => {
        if (!_.isUndefined(auth.authHeader)) {
            filterRoutes.forEach((route) => {
                let parameter = '';

                switch (route.id) {
                    case 'fnol':
                        parameter = 'AX_REPORT_CLAIM';
                        break;
                    case 'createService':
                        parameter = 'AX_REPORT_SERVICE';
                        break;
                    default:
                        break;
                }

                if (parameter !== '') {
                    ServiceRequestService.hasAuthorityAX(parameter, auth.authHeader)
                        .then((response) => {
                            if (!response) {
                                addRemoveRoute(route.id);
                            }
                        }).catch((error) => {
                            // eslint-disable-next-line no-console
                            console.log('Error: ', error);
                        });
                }
            });
        }
    }, [auth.authHeader, filterRoutes, addRemoveRoute]);

    useEffect(() => {
        if (!_.isUndefined(auth.authHeader)) {
            ServiceRequestService.getSystemParameterStringByNameAX('AX_RecoveryModuleURL', auth.authHeader)
                .then((url) => {
                    setPaymentAppURL(url);
                });
            routesAuthority();
        }
    }, [auth]);

    const getAvatarLinks = useCallback(() => {
        return [
            // intentionally commented out, this is not required for AXA
            // <DropdownMenuLink to="/faq">{translator(messages.faq)}</DropdownMenuLink>,
            <DropdownMenuLink href={paymentAppURL} target="_blank">{translator(messages.onlinePaymentApp_AX)}</DropdownMenuLink>,
            <DropdownMenuLink to="/preferences">{translator(messages.preferences)}</DropdownMenuLink>,
            <DropdownMenuLink onClick={auth.logout}>{translator(messages.logout)}</DropdownMenuLink>
        ];
    }, [translator, auth, paymentAppURL]);

    const getLogoSrc = useCallback(() => {
        const prefix = _.get(env, 'DEPLOYMENT_PATH', '').replace(/\/$/, '');
        return `${prefix}/branding/axa_logo_open_white_rgb.png`;
    }, []);

    // (Router) Title is required and used for (Browser) title
    // in a format of "${title} - ${appName}". In order to show
    // the application name only, it needs to be an empty value.
    const routesWithoutTitle = useMemo(() => {
        return filterCapabilityRoutes(capabilitiesConfig, filterRoutes)
            .map((route) => {
                return {
                    ...route,
                    title: _.isUndefined(route.id) ? route.title
                        : translator(_.get(messages, route.id))
                };
            });
    }, [filterRoutes,translator]);

    const floorPlansConfig = useMemo(() => [{
        scrollContent: false,
        routes: routesWithoutTitle,
        header: {
            avatarChildren: getAvatarLinks(),
            avatarProps: {
                username: auth.user_name,
                title: auth.user_name,
                subtitle: auth.name
            },
            useAuthInfo: false,
            className: 'appheader',
            logoSrc: getLogoSrc(),
            showAppSwitcher: false,
            showNotifications: false
        }
    }], [auth.name, auth.user_name, getAvatarLinks, getLogoSrc, routesWithoutTitle]);

    useEffect(
        () => {
            const translatorFn = vmTranslator(translator);
            import(
                /* webpackChunkName: "product-metadata" */
                // eslint-disable-next-line import/no-unresolved
                'product-metadata'
            ).then((productMetadata) => {
                const { default: result } = productMetadata;
                updateViewModelService(
                    ViewModelServiceFactory.getViewModelService(result, translatorFn)
                );
            });
        },
        [translator]
    );

    const handleError = useCallback((error) => {
        console.log('error:');
        console.log(error);
        showAlert({
            status: 'error',
            icon: 'mi-error-outline',
            title: 'An Error',
            message: error.message,
        }).then((result) => {
            if (result === 'confirm') {
                // TODO handle click on confirm
            }
        }).catch(_.noop);
    }, []);

    return (
        <AccurateBreakpointPropagation>
            <ViewModelServiceContext.Provider value={viewModelService}>
                <ErrorBoundary onError={handleError}>
                    <Router
                        basename="/vendor-engage"
                        getUserConfirmation={
                            (message, callback) => {
                                return routeConfirmationModal(message, callback, modalEmitter);
                            }
                        }
                    >
                        <Switch>
                            <Route exact path="/login-page" component={EntryPage} />
                            <Route path="/VendorEngage/auth/resetpassword" component={ResetPasswordPage} />
                            <Route path="/proveedores/auth/resetpassword" component={ResetPasswordPage} />
                            <Route path="/auth/resetpassword" component={ResetPasswordPage} />
                            <DynamicRoute path="/">
                                <AppFloorPlan
                                    componentMap={componentMap}
                                    floorPlans={floorPlansConfig}
                                />
                            </DynamicRoute>
                        </Switch>
                    </Router>
                </ErrorBoundary>
            </ViewModelServiceContext.Provider>
        </AccurateBreakpointPropagation>
    );
}

export default withAuthContextProvider()(App);
